// src/components/AssessmentIntro.jsx
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import translations from './translations';
import './AssessmentIntro.css';

function AssessmentIntro() {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
   // 从 URL 查询参数中获取语言，默认为簡体中文
  const defaultLang = searchParams.get('defaultLang') || 'zh-CN';
  const t = translations[defaultLang] || translations['zh-CN'];
  const [isStarting, setIsStarting] = useState(false);
  const [showResumeDialog, setShowResumeDialog] = useState(false);

  const handleStart = async () => {
    setIsStarting(true);
    
    try {
      // 从localStorage获取用户信息与当前二维码参数
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      const { useruuid, evaluation_uuid } = userInfo;

      const urlParams = new URLSearchParams(window.location.search);
      const companyCode = urlParams.get('companyCode') || '';
      const jobNID = urlParams.get('Job_NID') || '';
      const codeType = urlParams.get('codeType') || '';
      const evaluationType = urlParams.get('evaluation_type') || '';
      const orderKey = `quizOrder_${defaultLang}`;

      let sessions;
      try { sessions = JSON.parse(localStorage.getItem('userEvaluationSessions') || '{}'); } catch { sessions = {}; }
      const prev = useruuid ? sessions[useruuid] : undefined;
      const hasJobParams = !!(companyCode || jobNID || codeType || evaluationType);
      const existingAnswers = (() => { try { return JSON.parse(localStorage.getItem('quizAnswers')) || {}; } catch { return {}; } })();
      const hasExistingAnswers = Object.keys(existingAnswers).length > 0;

      // 1) 先处理存在历史作答的情况：根据二维码是否相同决定清理或接续
      if (hasExistingAnswers) {
        if (useruuid) {
          const isSame =
            prev &&
            prev.companyCode === companyCode &&
            prev.Job_NID === jobNID &&
            prev.codeType === codeType &&
            prev.evaluation_type === evaluationType;

          // 若存在岗位二维码且不同，则清理本地并直接进入新测评
          if (hasJobParams && (!prev || !isSame)) {
            localStorage.removeItem('quizAnswers');
            localStorage.removeItem(orderKey);
            localStorage.removeItem('startTime');
            localStorage.removeItem('timeTaken');
            sessions[useruuid] = { companyCode, Job_NID: jobNID, codeType, evaluation_type: evaluationType };
            localStorage.setItem('userEvaluationSessions', JSON.stringify(sessions));
            navigate(`/quiz?defaultLang=${defaultLang}`);
            return;
          }
        }
        // 二维码相同或无岗位参数：弹出接续对话框并结束，不调用后端
        setShowResumeDialog(true);
        return;
      }

      // 2) 无历史答案：更新会话并调用后端 start_evaluation，随后进入测评
      if (useruuid) {
        sessions[useruuid] = { companyCode, Job_NID: jobNID, codeType, evaluation_type: evaluationType };
        localStorage.setItem('userEvaluationSessions', JSON.stringify(sessions));
      }

      if (useruuid && evaluation_uuid) {
        // 调用start_evaluation API更新状态
        const API_BASE_URL = 'https://tpesapi3.properson.cn';
        const accessToken = 'a148stydfIOIUkdkfpx-1264893-asdfe8xh4l-123256ldkxLKHNC';
        
        const response = await axios.post(`${API_BASE_URL}/api/assessment/start_evaluation`, {
          useruuid: useruuid,
          evaluation_uuid: evaluation_uuid
        }, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        });
        
      }

      navigate(`/quiz?defaultLang=${defaultLang}`);
    } catch (error) {
      const existingAnswers = (() => { try { return JSON.parse(localStorage.getItem('quizAnswers')) || {}; } catch { return {}; } })();
      if (Object.keys(existingAnswers).length > 0) {
        setShowResumeDialog(true);
      } else {
        navigate(`/quiz?defaultLang=${defaultLang}`);
      }
    } finally {
      setIsStarting(false);
    }
  };

  return (
    <div className="assessment-intro">
      <h1>{t.assessmentTitle}</h1>
      <ul className="assessment-notes">
        <li>{t.networkNote}</li>
        <li>{t.focusNote}</li>
        <li>{t.choiceNote}</li>
      </ul>

      <div className="assessment-options">
        {[t.optionYes, t.optionMaybe, t.optionNo].map((option, index) => (
          <div key={index} className={`option-item option-${option.key}`}>
            <div className="option-title">{option.title}</div>
            <div className="option-description">{option.description}</div>
          </div>
        ))}
      </div>

      <ul className="assessment-notes">
        <li className="important-note">{t.startNote}</li>
      </ul>

      <button 
        className="start-button" 
        onClick={handleStart}
        disabled={isStarting}
      >
        {isStarting ? '正在开始...' : t.startButton}
      </button>
      {showResumeDialog && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '20px 24px', width: 'min(420px, 90vw)', boxShadow: '0 8px 24px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize: '16px', marginBottom: '16px' }}>{t.resumeDialog?.message || '检测到已有部分作答记录。请选择接续答题或重新开始。'}</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <button
                onClick={() => { setShowResumeDialog(false); navigate(`/quiz?defaultLang=${defaultLang}&resume=1`); }}
                className="start-button"
                style={{ width: '100%' }}
              >
                {t.resumeDialog?.continueButton || '接续答题'}
              </button>
              <button
                onClick={() => { const orderKey = `quizOrder_${defaultLang}`; localStorage.removeItem('quizAnswers'); localStorage.removeItem(orderKey); localStorage.removeItem('startTime'); localStorage.removeItem('timeTaken'); setShowResumeDialog(false); navigate(`/quiz?defaultLang=${defaultLang}&restart=1`); }}
                className="start-button"
                style={{ width: '100%', background: '#dc2626', color: '#fff', border: 'none' }}
              >
                {t.resumeDialog?.restartButton || '重新开始'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AssessmentIntro;
